/**
 * The QPay QR.
 *
 * The settle arrives over the socket — QPay calls the server's webhook, the
 * server verifies with QPay's own check endpoint, and the kiosk is told. The
 * old client polled `payment/check` on a timer.
 *
 * `qpayCheck` is the fallback for a kiosk the socket did not tell. The sheet
 * polls it on a long interval, and **Check payment** asks the same question
 * at once — a customer who has paid and is still staring at this QR, because
 * the webhook never landed or the socket was down.
 *
 * **The code has a visible life.** It used to have none: the sheet would sit
 * open forever showing a QR that QPay may have expired minutes ago, and an
 * expired code is pixel-identical to a live one. A customer scanning it gets
 * an error in their banking app, scans again, and has no way to tell whether
 * the fault is the code, their phone or the shop. So the remaining time is on
 * screen, and when it runs out the sheet says so and offers a fresh code.
 *
 * The listener, the poll and the check button deliberately keep running past
 * expiry. A customer who paid in the last second before it lapsed has moved
 * real money, and the settle must still be honoured — the expiry governs what
 * the *screen* claims, never whether a payment is accepted.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { renderSVG } from 'uqr'
import { format } from '@mgc/domain'
import type { Money } from '@mgc/domain'
import { AmountPill, Button, Sheet, Spinner } from '@/components/ui'
import { refusalOf, trpc } from '@/api/client'
import { useT } from '@/i18n'
import { useServerEvents } from '@/api/events'

/**
 * How long a code is offered for.
 *
 * Three minutes is far longer than scanning a QR and confirming in a banking
 * app actually takes, and short enough that a customer who wandered off and
 * came back is given a fresh code rather than a stale one.
 */
const QR_LIFETIME_MS = 3 * 60_000

const clock = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function QPayModal({
  qrText,
  paymentId,
  orderId,
  amount,
  onSettled,
  onFailed,
  onCancel,
  onRenew,
  onLiveChange
}: {
  qrText: string
  paymentId: string
  orderId: string
  amount: Money
  onSettled: () => void
  onFailed: (reason: string) => void
  onCancel: () => void | Promise<void>
  /** Cancel this invoice and issue another. */
  onRenew: () => void | Promise<void>
  /** Whether the code can still be paid — what holds the idle timer off. */
  onLiveChange: (live: boolean) => void
}) {
  const t = useT()
  const [settling, setSettling] = useState(false)
  const [renewing, setRenewing] = useState(false)
  const [checking, setChecking] = useState(false)
  const [notYet, setNotYet] = useState(false)
  const inFlight = useRef(false)
  const svg = useMemo(() => renderSVG(qrText, { border: 1 }), [qrText])

  /*
   * Keyed on `qrText` so a renewed code starts its own life. The deadline is a
   * timestamp rather than a counter because an interval in a backgrounded
   * webview does not tick reliably, and a countdown that drifts is worse than
   * none — it would claim a code is live after QPay has dropped it.
   */
  const [expiresAt, setExpiresAt] = useState(() => Date.now() + QR_LIFETIME_MS)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    setExpiresAt(Date.now() + QR_LIFETIME_MS)
    setNow(Date.now())
    setNotYet(false)
  }, [qrText])
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(tick)
  }, [])

  const remaining = expiresAt - now
  const expired = remaining <= 0 && !settling
  const busy = settling || renewing || checking

  const live = !expired
  useEffect(() => {
    onLiveChange(live)
  }, [live, onLiveChange])
  useEffect(() => () => onLiveChange(false), [onLiveChange])

  /**
   * Ask QPay whether this invoice is paid, then capture if it is.
   *
   * **`state === 'PAID'` is a request to capture, not the decision.** This
   * read QPay's own answer and asked for a capture that nothing re-checked, so
   * the amount guard in the callback — the reason the invoice is minted with
   * `allow_partial: false` — was absent on the path a kiosk actually takes
   * when the socket is down. `orders.setPaymentState` asks QPay itself now
   * and refuses a capture QPay will not stand behind, which is why the
   * failure is handled here rather than swallowed: a refusal on this path is
   * a customer who has paid *something*, and an attendant is the right answer
   * to that.
   *
   * `announce` is the button. The silent poll must not flash "not yet" every
   * eight seconds on a sheet that is already waiting.
   */
  const confirmIfPaid = useCallback(
    async (announce: boolean) => {
      if (inFlight.current || settling) return
      inFlight.current = true
      if (announce) {
        setChecking(true)
        setNotYet(false)
      }
      try {
        const state = await trpc.payments.qpayCheck.query({ paymentId })
        if (state.state !== 'PAID') {
          if (announce) setNotYet(true)
          return
        }
        setSettling(true)
        try {
          await trpc.orders.setPaymentState.mutate({ orderId, paymentId, state: 'captured' })
        } catch (error) {
          setSettling(false)
          const refusal = refusalOf(error)
          if (refusal === 'qpay-not-settled') onFailed(refusal)
          return
        }
        onSettled()
      } catch {
        if (announce) setNotYet(true)
      } finally {
        inFlight.current = false
        if (announce) setChecking(false)
      }
    },
    [orderId, paymentId, settling, onSettled, onFailed]
  )

  // The mechanism, and its other half. A customer standing in front of a QR
  // code that has failed deserves to be told; silence is the worst of the
  // three outcomes.
  useServerEvents((event) => {
    if (event.type === 'payment-settled' && event.orderId === orderId) {
      setSettling(true)
      onSettled()
    }
    /*
     * This code's payment, not merely this order's. An order carries one QPay
     * row per code shown, and a failure about the one the customer cancelled
     * closed the live code — over a raw QPay state (`cancelled`, `expired`)
     * that has no string and read «Алдаа гарлаа». The reason goes to the log in
     * `app.tsx`; the customer is told the one thing they can act on.
     */
    if (event.type === 'payment-failed' && event.paymentId === paymentId) onFailed('qpay-not-settled')
  })

  useEffect(() => {
    const timer = setInterval(() => {
      void confirmIfPaid(false)
    }, 8_000)
    return () => clearInterval(timer)
  }, [confirmIfPaid])

  return (
    <Sheet open labelledBy="qpay-title">
      <div className="flex flex-col items-center gap-6 overflow-y-auto overscroll-contain px-10 py-10 text-center">
        <h2 id="qpay-title" className="display text-[length:var(--text-kiosk-xl)] uppercase">
          {t('payment.qpayScan')}
        </h2>

        {/*
          The code on a white plate with a wide margin. A phone camera needs the
          quiet zone, and a QR flush to a tinted card is one a customer holds
          their phone at for ten seconds before an attendant comes over.
        */}
        <div className="relative">
          <div
            className={`rounded-[var(--radius-tile)] border-8 border-[var(--color-paper-sunk)] bg-white p-8 shadow-[var(--shadow-tile)] [&>svg]:size-72 ${
              expired ? 'opacity-20' : ''
            }`}
            // uqr returns a complete SVG document built from the payload; there is
            // no user-supplied markup in it.
            dangerouslySetInnerHTML={{ __html: svg }}
          />
          {/*
            The dead code stays on screen underneath, greyed. Removing it
            entirely reads as a crash; leaving it legible invites another scan.
          */}
          {expired && (
            <div className="absolute inset-0 grid place-items-center">
              <p className="display text-center text-[length:var(--text-kiosk-lg)] text-[var(--color-bad)] uppercase">
                {t('payment.qpayExpired')}
              </p>
            </div>
          )}
        </div>

        <AmountPill>{format(amount)}</AmountPill>

        {settling ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <div className="flex w-full flex-col items-center gap-8">
            {!expired && (
              <div className="flex flex-col items-center gap-2">
                <p className="display-upright text-[length:var(--text-kiosk)] uppercase">
                  {notYet ? t('payment.qpayNotYet') : t('payment.qpayWaiting')}
                </p>
                {/*
                  Polite and atomic: it changes every second, and a screen reader
                  reading each tick over the instruction above would be worse than
                  not announcing it at all. Most readers coalesce a polite region
                  that changes this often; the sentence above is the one that has
                  to land.
                */}
                <p className="eyebrow" aria-live="off">
                  {t('payment.qpayExpiresIn')} <span className="tabular-nums">{clock(remaining)}</span>
                </p>
              </div>
            )}

            {expired && notYet && (
              <p className="display-upright text-[length:var(--text-kiosk)] uppercase">{t('payment.qpayNotYet')}</p>
            )}

            <div className="flex w-full flex-col gap-3">
              {expired && (
                <Button
                  size="large"
                  className="w-full uppercase"
                  disabled={busy}
                  onClick={() => {
                    setRenewing(true)
                    void Promise.resolve(onRenew()).finally(() => setRenewing(false))
                  }}
                >
                  {t('payment.qpayRenew')}
                </Button>
              )}
              <Button
                size="large"
                variant={expired ? 'ghost' : 'primary'}
                className="w-full uppercase"
                disabled={busy}
                onClick={() => void confirmIfPaid(true)}
              >
                {checking ? t('common.loading') : t('payment.qpayCheck')}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => void onCancel()} disabled={busy}>
                {t('payment.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  )
}
